// The single kratos error reader for the whole app.
//
// Batch 005 established it inside pages/profile/profile.utils.ts. Batch 007 /
// ADR-B4 (closing DEF-004-07) moved it here, because by then FOUR importers
// already lived outside that page — including services/authed.query.ts, the
// TRANSPORT layer — so `services → pages/profile` was a back-edge from the
// bottom of the stack into a screen. Nothing here imports a page: the field
// whitelist is a caller-supplied list of plain strings, not a form's key type.
//
// The new BE speaks kratos: the response body carries {code, reason, message,
// metadata} at the top level, and per 005 ADR-2's field-error mapping rule every
// attributable 4xx puts the contract field name in `metadata.key`. This is NOT
// the legacy BFF's {error: {detail: {key, text}}} — reading that shape against
// the new BE yields undefined and silently swallows the error (found by the 005
// integration gate).

// Default inline-able fields: the profile form's set, kept as the default
// because three callers (authed.query's 500 branch and the two password-reset
// pages) read only the copy and never inline, and changing what they resolve
// would change the sentence the user reads.
//
// Declared as plain strings rather than `keyof ProfileFormValues`: importing
// that type is exactly the back-edge this move removes. A caller that DOES
// inline passes its own literal list, which makes F infer to its own field
// union and keeps setError type-safe at the call site.
const DEFAULT_ERROR_FIELDS = [
  'username',
  'first_name',
  'last_name',
  'email',
  'phone',
  'old_password',
  'new_password',
] as const;

// Frozen copy (UI/UX freeze): the legacy BFF sent an error code as
// `detail.text` and the pages ran it through lt(), so those codes are i18n keys
// in this repo. Keeping the same key per (status, field) keeps the wording the
// user sees identical to the legacy screens: EmailIsDuplicatedErr
// (i18n/en.json:442), UserNameIsDuplicatedErr (:456), UserOldPWDErr (:467,
// "Incorrect current password"), and — for the new server-side policy check
// (005 ADR-4, 422) — the existing FE password-rule copy (:380). Pairs with no
// legacy counterpart (e.g. SSO-only username change, a new-BE-only 400) fall
// through to the server message rather than inventing copy.
//
// '409:cpr' is batch 007 / ADR-B4. Same provenance, not new wording: the legacy
// duplicate-CPR path answered Detail{Key:"cpr", Text: CPRIsDuplicatedErr}
// (rpms-service/apps/bff/api/internal/logic/dashboard/patientadd.go:69 and
// patientedit.go:153), and that key is present in both locales
// (i18n/en.json:457, da.json:461).
const FROZEN_COPY: Record<string, string> = {
  '409:email': 'EmailIsDuplicatedErr',
  '409:username': 'UserNameIsDuplicatedErr',
  '409:cpr': 'CPRIsDuplicatedErr',
  '400:old_password': 'UserOldPWDErr',
  '422:new_password': 'cdb_admin_form_verification_pwd_format',
};

// Reason literals — a CROSS-REPO CONTRACT, pinned verbatim against the rpms
// `errorx` TypeString constants (pkg/x/errorx/constant.go). Do not reword
// these, and do not "tidy" them into the enum-looking Go identifier names — the
// wire value is the human-readable string, not the constant's name. (The
// mapping is editorial, not mechanical: `UpstreamError` is "UPSTREAM
// UNAVAILABLE" and `LockedError` is just "LOCKED", so neither literal below can
// be derived from its Go identifier — both were read off the wire.)
//
// Both strings were VERIFIED against the live backend in the assembled-system
// gate (2026-07-29): equality assertions on the `reason` of a real 409 from
// each path. Getting one wrong is silent — the mapper would fall through to the
// generic "try again later" and the whole point of distinguishing them is lost —
// so re-verify rather than assume if the backend ever renames them.
//
// Batch 006 needs them because THREE different server conditions arrive as
// 409 + metadata.key="email" (employee.proto ERROR CONTRACT) and the admin's
// correct next action differs for each:
//   CONFLICT           → the address is taken by another user  → pick another
//   SSO ACCOUNT EXISTS → it already belongs to an SSO account  → stop, use SSO
//   SSO EMAIL IMMUTABLE→ this account's email is IdP-owned     → cannot change
export const SSO_ACCOUNT_EXISTS = 'SSO ACCOUNT EXISTS';
export const SSO_EMAIL_IMMUTABLE = 'SSO EMAIL IMMUTABLE';

// reason → frozen copy. Checked BEFORE the (status, field) table: the reason
// describes the situation rather than the field, so it must win even when the
// caller does not recognise the metadata key.
const REASON_COPY: Record<string, string> = {
  [SSO_ACCOUNT_EXISTS]: 'cdb_admin_email_sso_account_exists',
  [SSO_EMAIL_IMMUTABLE]: 'cdb_admin_email_sso_immutable',
};

const GENERIC_ERROR_KEY = 'common_error_try_again_later';

export type RequestErrorDisplay<F extends string = string> = {
  // Form field to inline the error on; absent when the error is not attributable.
  field?: F;
  // i18n key to render via lt() — a frozen local string.
  i18nKey?: string;
  // Server message, rendered as-is when there is no frozen copy for the pair
  // (it is English prose, not an i18n key — do not run it through lt()).
  text?: string;
};

// `fields` is the caller's set of inline-able form field names, defaulting to
// the profile page's. It is a parameter because contract field names are per
// surface: the admin form's phone field is `cell_phone` (INV-D's 400 names it)
// while the profile form's is `phone`, so a single hardcoded list would leave
// one of the two surfaces unable to inline its own errors.
export const mapRequestError = <F extends string = string>(
  error: unknown,
  fields: readonly F[] = DEFAULT_ERROR_FIELDS as readonly string[] as readonly F[]
): RequestErrorDisplay<F> => {
  const rejected = error as
    | {
        status?: number | string;
        data?: {
          code?: number;
          reason?: string;
          message?: string;
          metadata?: { key?: string };
        };
      }
    | undefined;
  const body = rejected?.data;
  const key = body?.metadata?.key;
  const field = fields.find(name => name === key);

  const byReason = body?.reason ? REASON_COPY[body.reason] : undefined;
  if (byReason) {
    return { field, i18nKey: byReason };
  }

  if (!key) {
    // Not attributable to a request field (5xx, transport failure, or a legacy
    // BFF-shaped payload): a toast with the generic copy, no inline error.
    return { i18nKey: GENERIC_ERROR_KEY };
  }

  const status = rejected?.status ?? body?.code;
  const frozen = field ? FROZEN_COPY[`${status}:${field}`] : undefined;
  if (frozen) {
    return { field, i18nKey: frozen };
  }
  // No frozen copy for this pair — surface the server text (fail loud) and
  // inline it only when the key names a form field.
  return field ? { field, text: body?.message } : { text: body?.message };
};
